// One analytics screen (SPEC-v2 A1; API.md 6.1 and endpoint 87). Every figure
// is worked out from the record. Counting helpers: ../disaggregation.ts.
import { z } from "zod";
import type { ExpertCategory } from "../enums";
import type { CountItem } from "../disaggregation";
import { isoDate, uuid } from "./common";

/** 87. GET /api/analytics?contractId&from&to (default: the last 12 months up to today). */
export const analyticsQuery = z
  .object({
    contractId: uuid.optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.from && v.to && v.to < v.from) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: "The end date must be on or after the start date." });
    }
  });
export type AnalyticsQuery = z.infer<typeof analyticsQuery>;

export type Analytics = {
  filters: { contractId: string | null; from: string | null; to: string | null };
  participation: {
    eventsHeld: number;
    registered: number;
    attended: number;
    attendanceRatePct: number | null;
    uniqueParticipants: number;
    newContacts: number;
  };
  reach: { organisations: number; countries: number };
  /** all five genders + "none", always present */
  byGender: CountItem[];
  /** top 15 by count, then key "other", then "none" */
  byCountry: CountItem[];
  /** all seven types + "none", always present */
  byStakeholderType: CountItem[];
  /** ≤ 12, oldest first; month = "YYYY-MM" */
  growth: { month: string; attended: number; newContacts: number }[];
  expertDays: {
    planned: number;
    logged: number;
    /** ≤ 12, oldest first */
    byMonth: { month: string; days: number }[];
    byCategory: { category: ExpertCategory; label: string; days: number }[];
  };
  deliverables: {
    planned: number;
    inProgress: number;
    delivered: number;
    overdue: number;
    /** Σ latest value per deliverable and label */
    dataPoints: { label: string; total: number }[];
  };
  /** only when contractId is given; else [] */
  indicators: {
    id: string;
    code: string;
    name: string;
    unit: string;
    baseline: number | null;
    target: number | null;
    latest: number | null;
    progressPct: number | null;
  }[];
};
